import { FC, useState, useEffect, useRef, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchBar from 'src/components/InputFields/Searchbar';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { useFormik } from 'formik';
import StyledGridContainer, {
  CenteredCellBox,
} from 'src/components/StyledComponents/StyledGridContainer';
import StyledDataGrid from 'src/components/StyledComponents/StyledDataGrid';
import CustomNoRowsOverlay from 'src/components/StyledComponents/CustomEmptyTableRow';
import LoadingTable from 'src/components/StyledComponents/CustomLoadingTable';
import PaginationControl from 'src/components/StyledComponents/StyledPagination';
import FilterPopover from 'src/components/StyledComponents/FilterPopover';
import FormikSelectField, {
  Option,
} from 'src/components/InputFields/FormikSelectField';
import CreatedDatePicker from 'src/components/InputFields/DatePicker';
import Status from 'src/components/Status';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { formatDateYYYYMMDD } from 'src/utils/format.utils';
import { pagination } from 'src/utils/properties.utils';
import { SortOrder } from 'src/models/Sort.types';
import {
  getVoucherChequePayments,
  VoucherChequePayment,
  VoucherChequePaymentFilters,
  updateVoucherChequeStatus,
} from 'src/services/chequePayment.service';
import {
  ChequeStatus,
  getChequeStatusLabel,
} from 'src/models/enums/ChequeStatus.enum';
import {
  FilterButton,
  FilterIcon,
  FilterPopoverIcon,
  FilterPopoverTitle,
  FilterPopoverDivider,
  FilterCloseIcon,
  FilterLabel,
  SmallActionButton,
  SpacedContainer,
} from 'src/components/StyledComponents/StyledPageComponents';
import {
  GridActionButton as UpdateButton,
  GridActionButton as ViewButton,
  TabContentContainer,
  SearchFilterRow,
  FilterPopoverHeader,
  FilterTitleRow,
  FilterSection,
  DatePickerContainer,
  FilterActionRow,
  FilterLabelWithMargin,
} from './ChequeStatus.styles';
import UpdateChequeStatusDialog from 'src/components/DialogBox/UpdateChequeStatusDialog/UpdateChequeStatusDialog';
import InfoDialog from 'src/components/DialogBox/InfoDialog/InfoDialog';

interface FilterFormValues {
  chequeStatusPicker: string | number;
  chequeStartDate: Date | null;
  chequeEndDate: Date | null;
  paymentStartDate: Date | null;
  paymentEndDate: Date | null;
}

const VouchersChequeTab: FC = () => {
  const navigate = useNavigate();
  const { showSnackbar } = StyledSnackbar();
  const showSnackbarRef = useRef(showSnackbar);
  showSnackbarRef.current = showSnackbar;

  // State
  const [payments, setPayments] = useState<VoucherChequePayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(pagination.DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState(pagination.DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState<string>('chequeDate');
  const [order, setOrder] = useState<SortOrder>(SortOrder.DESC);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'chequeDate', sort: 'desc' },
  ]);
  const [refreshKey, setRefreshKey] = useState(0);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog state
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] =
    useState<VoucherChequePayment | null>(null);

  // Filter state
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [dateFilterType, setDateFilterType] = useState<string>('chequeDate');
  const [appliedFilters, setAppliedFilters] =
    useState<VoucherChequePaymentFilters>({});

  // Fetch payments
  const fetchPayments = async () => {
    setLoading(true);
    try {
      const filters = {
        ...appliedFilters,
        search: searchTerm || undefined,
      };
      const response = await getVoucherChequePayments(
        page,
        pageSize,
        sortBy,
        order.toUpperCase() as 'ASC' | 'DESC',
        filters
      );
      setPayments(response.data || []);
      setTotalItems(response.totalItems || 0);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbarRef.current(errorMessage, 'error');
      setPayments([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [page, pageSize, sortBy, order, appliedFilters, refreshKey, searchTerm]);

  // Cheque status options
  const chequeStatusOptions: Option[] = [
    { label: 'All Statuses', value: '' },
    { label: 'Issued', value: ChequeStatus.ISSUED },
    { label: 'Realized', value: ChequeStatus.REALIZED },
    { label: 'Returned', value: ChequeStatus.RETURNED },
    { label: 'Cancelled', value: ChequeStatus.CANCELLED },
  ];

  // Date filter options
  const dateFilterOptions: Option[] = [
    { label: 'Cheque Date', value: 'chequeDate' },
    { label: 'Payment Date', value: 'paymentDate' },
  ];

  const getDateFilterLabel = () => {
    switch (dateFilterType) {
      case 'paymentDate':
        return 'Payment Date:';
      case 'chequeDate':
      default:
        return 'Cheque Date:';
    }
  };

  // Filter formik
  const filterFormik = useFormik<FilterFormValues>({
    initialValues: {
      chequeStatusPicker: '',
      chequeStartDate: null,
      chequeEndDate: null,
      paymentStartDate: null,
      paymentEndDate: null,
    },
    onSubmit: (values) => {
      const filters: VoucherChequePaymentFilters = {
        chequeStatus: values.chequeStatusPicker || undefined,
      };

      if (dateFilterType === 'chequeDate') {
        if (values.chequeStartDate) {
          filters.chequeStartDate = formatDateYYYYMMDD(
            values.chequeStartDate.toISOString()
          );
        }
        if (values.chequeEndDate) {
          filters.chequeEndDate = formatDateYYYYMMDD(
            values.chequeEndDate.toISOString()
          );
        }
      } else if (dateFilterType === 'paymentDate') {
        if (values.paymentStartDate) {
          filters.paymentStartDate = formatDateYYYYMMDD(
            values.paymentStartDate.toISOString()
          );
        }
        if (values.paymentEndDate) {
          filters.paymentEndDate = formatDateYYYYMMDD(
            values.paymentEndDate.toISOString()
          );
        }
      }

      setAppliedFilters(filters);
      setPage(pagination.DEFAULT_PAGE);
      setFilterOpen(false);
    },
  });

  const handleFilterClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setFilterOpen(true);
  };

  const handleClose = () => {
    setFilterOpen(false);
    setAnchorEl(null);
  };

  const handleClear = () => {
    filterFormik.resetForm();
    setDateFilterType('chequeDate');
    setAppliedFilters({});
    setPage(pagination.DEFAULT_PAGE);
    setFilterOpen(false);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
    const sort = model[0];
    setPage(pagination.DEFAULT_PAGE);
    setSortBy(sort?.field || 'chequeDate');
    setOrder((sort?.sort as SortOrder) || SortOrder.DESC);
  };

  const handleSearchChange = (newQuery: string) => {
    setSearchTerm(newQuery);
    setPage(pagination.DEFAULT_PAGE);
  };

  const handleUpdateClick = (payment: VoucherChequePayment) => {
    if (payment.paymentStatus === 'Pending Approval') {
      setInfoDialogOpen(true);
    } else {
      setSelectedPayment(payment);
      setUpdateDialogOpen(true);
    }
  };

  const handleCloseUpdateDialog = () => {
    setUpdateDialogOpen(false);
    setSelectedPayment(null);
  };

  const handleUpdateSuccess = () => {
    setRefreshKey((prev) => prev + 1);
  };

  // Grid columns
  const columns: GridColDef[] = [
    {
      field: 'chequeNumber',
      headerName: 'Cheque Number',
      flex: 1,
      sortable: false,
    },
    {
      field: 'chequeDate',
      headerName: 'Cheque Date',
      flex: 1,
      renderCell: (params) =>
        params.value ? formatDateYYYYMMDD(params.value) : 'N/A',
    },
    {
      field: 'chequeRealizedDate',
      headerName: 'Realized Date',
      flex: 1,
      sortable: false,
      renderCell: (params) =>
        params.value ? formatDateYYYYMMDD(params.value) : 'N/A',
    },
    {
      field: 'paymentDate',
      headerName: 'Payment Date',
      flex: 1,
      sortable: false,
      renderCell: (params) =>
        params.value ? formatDateYYYYMMDD(params.value) : 'N/A',
    },
    {
      field: 'chequeStatus',
      headerName: 'Status',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <CenteredCellBox>
          {params.value != null ? (
            <Status status={getChequeStatusLabel(params.value)} />
          ) : (
            '—'
          )}
        </CenteredCellBox>
      ),
    },
    {
      field: 'payee',
      headerName: 'Payee',
      flex: 1.2,
      sortable: false,
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1.2,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <SpacedContainer display="flex" gap="8px" justifyContent="center">
          <UpdateButton
            onClick={() => handleUpdateClick(params.row)}
            disabled={params.row.paymentStatus === 'Rejected'}
          >
            Update
          </UpdateButton>
          <ViewButton
            onClick={() =>
              window.open(
                `/users/voucher-management?view=${params.row.voucherId}`,
                '_blank'
              )
            }
            bgColor="#2e7d32"
            hoverBgColor="#1b5e20"
          >
            View
          </ViewButton>
        </SpacedContainer>
      ),
    },
  ];

  const rows = payments.map((payment) => ({
    id: payment.id,
    chequeNumber: payment.chequeNumber,
    chequeDate: payment.chequeDate,
    chequeRealizedDate: payment.chequeRealizedDate,
    paymentDate: payment.paymentDate,
    chequeStatus: payment.chequeStatus,
    payee: payment.payee,
    voucherId: payment.voucherId,
    paymentStatus: payment.paymentStatus,
  }));

  return (
    <TabContentContainer>
      {/* Search and Filter Row */}
      <SearchFilterRow>
        <SearchBar
          id="voucherChequeSearch"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by payee or cheque number"
          width="280px"
        />
        <FilterButton
          className="btn-filter"
          width="100px"
          marginBottom="22px"
          onClick={handleFilterClick}
        >
          <FilterIcon component="i" className="bx bx-filter" />
          Add Filter
        </FilterButton>
      </SearchFilterRow>

      {/* Filter Popover */}
      <FilterPopover
        open={filterOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
      >
        <FilterPopoverHeader>
          <FilterTitleRow>
            <FilterPopoverIcon component="i" className="bx bx-filter" />
            <FilterPopoverTitle>Filter Data By</FilterPopoverTitle>
          </FilterTitleRow>
          <FilterPopoverDivider />
          <FilterCloseIcon
            component="i"
            className="bx bx-x-circle"
            onClick={handleClose}
          />
        </FilterPopoverHeader>
        <FilterSection>
          <FormikSelectField
            id="chequeStatusPicker"
            label="Cheque Status"
            height="28px"
            width="100%"
            customMessage="Status"
            textColor="var(--subtext)"
            borderColor="var(--hover-white)"
            fontSize="10px"
            fontWeight="400"
            showLabel={false}
            formikProps={filterFormik}
            options={chequeStatusOptions}
          />
        </FilterSection>
        <FilterSection>
          <FilterLabelWithMargin>Select Date Filter</FilterLabelWithMargin>
          <FormikSelectField
            id="dateFilterTypePicker"
            label="Date Filter"
            height="28px"
            width="100%"
            customMessage="Select Date Filter"
            textColor="var(--subtext)"
            borderColor="var(--hover-white)"
            fontSize="10px"
            fontWeight="400"
            showLabel={false}
            formikProps={{
              ...filterFormik,
              values: {
                ...filterFormik.values,
                dateFilterTypePicker: dateFilterType,
              },
              handleChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                setDateFilterType(e.target.value);
              },
            }}
            options={dateFilterOptions}
          />
        </FilterSection>
        <DatePickerContainer>
          <FilterLabel>{getDateFilterLabel()}</FilterLabel>
          <SpacedContainer display="flex" justifyContent="space-between">
            <CreatedDatePicker
              name={
                dateFilterType === 'paymentDate'
                  ? 'paymentStartDate'
                  : 'chequeStartDate'
              }
              placeholder="Start Date"
              formikProps={filterFormik}
              fontSize="10px"
            />
            <CreatedDatePicker
              name={
                dateFilterType === 'paymentDate'
                  ? 'paymentEndDate'
                  : 'chequeEndDate'
              }
              placeholder="End Date"
              formikProps={filterFormik}
              fontSize="10px"
            />
          </SpacedContainer>
        </DatePickerContainer>
        <FilterActionRow>
          <SmallActionButton
            btnVariant="inactive"
            width="70px"
            onClick={handleClear}
          >
            Clear
          </SmallActionButton>
          <SmallActionButton
            className="btn-primary"
            width="70px"
            onClick={() => filterFormik.handleSubmit()}
          >
            Apply
          </SmallActionButton>
        </FilterActionRow>
      </FilterPopover>

      {/* Data Grid */}
      {loading ? (
        <LoadingTable columns={columns} />
      ) : (
        <StyledGridContainer
          headerHeight="258px"
          pagination={
            totalItems > 0 ? (
              <PaginationControl
                totalItems={totalItems}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                loading={loading}
              />
            ) : undefined
          }
        >
          <StyledDataGrid
            rows={rows}
            columns={columns}
            sortingMode="server"
            sortModel={sortModel}
            sortingOrder={['asc', 'desc']}
            onSortModelChange={handleSortModelChange}
            disableColumnMenu
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
            }}
          />
        </StyledGridContainer>
      )}

      {/* Update Cheque Status Dialog */}
      <UpdateChequeStatusDialog
        open={updateDialogOpen}
        onClose={handleCloseUpdateDialog}
        data={selectedPayment}
        onSuccess={handleUpdateSuccess}
        onUpdate={updateVoucherChequeStatus}
      />

      {/* Info Dialog */}
      <InfoDialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        title="Information"
        subtitle="This payment has not yet been approved, so the cheque status cannot be updated."
      />
    </TabContentContainer>
  );
};

export default VouchersChequeTab;
